// Rev Share Planner — internal revenue-sharing planning math (Football).
// Planning figures (group refs, player estimates, custom amounts) are stored at BASE-budget scale and
// shown × scale (scenario budget ÷ base budget). Real dollars — signed commitments (Funding Stack
// "Rev Share" lines) and player/agent requests — never scale.

export const REVSHARE_VIEW_ROLES = ["chief_of_staff", "super_admin", "athletic_director", "deputy_ad", "gm", "head_coach", "nil", "compliance"];

export const IMPACT_TIERS = [
  ["developmental", "Developmental / Depth"], ["contributor", "Contributor"],
  ["core_starter", "Core Starter"], ["difference_maker", "Difference-Maker"],
];
export const LMH = [["low", "Low"], ["moderate", "Moderate"], ["high", "High"]];
export const TARGET_MODES = [["lower", "Lower"], ["middle", "Middle"], ["upper", "Upper"], ["custom", "Custom"]];
export const ALLOC_BASES = [["target", "Target est."], ["low", "Low est."], ["high", "High est."], ["suggested", "Suggested"], ["custom", "Custom"]];
export const REQUEST_SOURCES = [["player", "Player"], ["agent", "Agent / representative"], ["family", "Family"], ["other", "Other"]];

export const RANGE_DEFS = {
  lower: "LOWER END: Adequate or replaceable production, available depth, and no established difference-maker.",
  middle: "MIDDLE: Dependable starters with meaningful roles who would be difficult to replace.",
  upper: "UPPER END: Exceptional impact, scarce replacement options, or a player who significantly changes how opponents prepare.",
};

// Default budget group from the roster position; ambiguous spots (ATH, generic OL/DL/LB/DB) need a staff pick.
const POS_GROUP = {
  QB: "QB",
  RB: "RB", FB: "RB", HB: "RB", TB: "RB",
  WR: "WR", X: "WR", Z: "WR", SLOT: "WR", SL: "WR",
  TE: "TE", Y: "TE", H: "TE",
  OT: "OT", LT: "OT", RT: "OT", T: "OT",
  OG: "IOL", LG: "IOL", RG: "IOL", G: "IOL", OC: "IOL", C: "IOL",
  DE: "EDGE", EDGE: "EDGE", OLB: "EDGE", JACK: "EDGE", BUCK: "EDGE",
  DT: "IDL", NT: "IDL", NG: "IDL", "3T": "IDL",
  MLB: "ILB", ILB: "ILB", MIKE: "ILB", WILL: "ILB", MAC: "ILB",
  CB: "CB", NB: "CB", NCB: "CB", NICKEL: "CB",
  S: "S", FS: "S", SS: "S", SAF: "S", ROV: "S",
  K: "K", PK: "K", P: "P", LS: "LS",
};
export const defaultGroupFor = (position) => POS_GROUP[(position || "").toUpperCase().trim()] || "";

export const money = (n) => (n == null || Number.isNaN(n) ? "—" : `${n < 0 ? "−" : ""}$${Math.round(Math.abs(n)).toLocaleString("en-US")}`);
export const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

// Signed annual revenue-share commitment = the player's Funding Stack "Rev Share" lines.
export const signedOf = (a) => (a?.funding_sources || []).filter((f) => f.type === "revenue_share").reduce((s, f) => s + (Number(f.amount) || 0), 0);

export const midOf = (g) => ((Number(g.lower) || 0) + (Number(g.upper) || 0)) / 2;

export function scaleOf(scenario, config) {
  const base = Number(config?.base_budget) || 100000;
  return (Number(scenario?.budget) || base) / base;
}

// Group planning target at base scale (reference only — never added to spending).
export function groupTargetBase(g, t) {
  const mode = t?.mode || "custom";
  if (mode === "lower") return Number(g.lower) || 0;
  if (mode === "upper") return Number(g.upper) || 0;
  if (mode === "middle") return midOf(g);
  return t?.custom_base != null ? Number(t.custom_base) : Number(g.starting) || 0;
}

// Player's planned allocation (actual $ at scenario scale) for a basis; null = Not assessed.
// Not in plan → $0 in every view, unless this scenario gives a deliberate Custom what-if amount.
// "suggested" uses the player's Worth Score dollar value (base scale).
export function plannedFor(assess, alloc, k, basisOverride, suggestedBase = null) {
  const basis = basisOverride || alloc?.basis || "target";
  const whatIf = basis === "custom" && alloc?.custom_base != null;
  if (assess?.not_in_plan && !whatIf) return 0;
  const base = basis === "custom" ? alloc?.custom_base : basis === "suggested" ? suggestedBase : assess?.[`est_${basis}`];
  return base == null ? null : base * k;
}

export const hasEstimates = (as) => ["est_low", "est_target", "est_high"].some((f) => as?.[f] != null);
export const rowStatus = (r) => (r.nip ? "Not in plan" : !r.costed ? "Not assessed" : "Planned");

// Signed deals count once; a planned figure above a signed deal is only the increment above it.
export function countedOf(planned, signed) {
  if (signed > 0) return { counted: Math.max(planned ?? 0, signed), below: planned != null && planned < signed, increment: planned != null ? Math.max(0, planned - signed) : 0, costed: true };
  return { counted: planned ?? 0, below: false, increment: 0, costed: planned != null };
}

export const groupOf = (a, as) => as?.budget_group || defaultGroupFor(a.position);

export function playerRows(players, assessments, scenario, k, worth = {}) {
  const byAth = Object.fromEntries((assessments || []).map((x) => [x.athlete_id, x]));
  return players.map((a) => {
    const as = byAth[a.id] || null;
    const alloc = scenario?.allocations?.[a.id] || null;
    const signed = signedOf(a);
    const planned = plannedFor(as, alloc, k, undefined, worth[a.id]?.base ?? null);
    const c = countedOf(planned, signed);
    const nip = !!as?.not_in_plan;
    const whatIf = nip && alloc?.basis === "custom" && alloc?.custom_base != null;
    return { a, as, alloc, group: groupOf(a, as), signed, planned, ...c, nip, whatIf, below: c.below && (!nip || whatIf), worth: worth[a.id] || null };
  });
}

export function groupSummaries(groups, rows, scenario, k) {
  const list = [...groups, { key: "", label: "Needs group", lower: 0, upper: 0, starting: 0, virtual: true }];
  return list.map((g) => {
    const rs = rows.filter((r) => r.group === g.key);
    const sum = (f) => rs.reduce((s, r) => s + (!r.nip && r.as?.[f] != null ? r.as[f] * k : 0), 0);
    const total = rs.reduce((s, r) => s + r.counted, 0);
    const target = g.virtual ? 0 : groupTargetBase(g, scenario?.group_targets?.[g.key]) * k;
    const upper = (Number(g.upper) || 0) * k;
    return {
      g, rows: rs, players: rs.length, assessed: rs.filter((r) => r.costed).length,
      lower: (Number(g.lower) || 0) * k, middle: midOf(g) * k, upper, target,
      sumLow: sum("est_low"), sumTarget: sum("est_target"), sumHigh: sum("est_high"),
      total, diff: target - total, exception: !g.virtual && total > upper + 0.5,
    };
  }).filter((s) => !s.g.virtual || s.players > 0);
}

export function budgetMath(scenario, rows) {
  const budget = Number(scenario?.budget) || 0;
  const reserveFull = budget * (Number(scenario?.reserve_pct) || 0) / 100;
  const release = Math.min(reserveFull, Number(scenario?.reserve_release) || 0);
  const reserve = reserveFull - release;
  const allocated = rows.reduce((s, r) => s + r.counted, 0);
  const signed = rows.reduce((s, r) => s + r.signed, 0);
  const unassessed = rows.filter((r) => !r.costed).length;
  const notInPlan = rows.filter((r) => r.nip).length;
  const status = allocated > budget + 0.5 ? "red" : allocated > budget - reserve + 0.5 ? "amber" : "green";
  return {
    budget, reserveFull, release, reserve, allocated, signed, unassessed, notInPlan, status,
    unallocated: budget - allocated, availableAfterReserve: budget - reserve - allocated,
    reserveUsed: Math.max(0, Math.min(reserve, allocated - (budget - reserve))),
  };
}

// Low / Target / High views from staff estimates (missing estimates are NOT zeros — partial projection).
export function comparisonFor(basis, players, assessments, scenario, k, worth = {}) {
  const byAth = Object.fromEntries((assessments || []).map((x) => [x.athlete_id, x]));
  let total = 0, missing = 0;
  players.forEach((a) => {
    const planned = plannedFor(byAth[a.id], scenario?.allocations?.[a.id], k, basis, worth[a.id]?.base ?? null);
    const c = countedOf(planned, signedOf(a));
    total += c.counted;
    if (!c.costed) missing += 1;
  });
  const m = budgetMath(scenario, []);
  return { total, missing, surplus: m.budget - total, reserveUsed: Math.max(0, Math.min(m.reserve, total - (m.budget - m.reserve))), status: total > m.budget + 0.5 ? "red" : total > m.budget - m.reserve + 0.5 ? "amber" : "green" };
}

export const STATUS_META = {
  green: { label: "Within budget — reserve protected", color: "#16a34a" },
  amber: { label: "Using the protected reserve", color: "#f59e0b" },
  red: { label: "Over budget", color: "#ef4444" },
};

export function blankScenario(config) {
  const base = Number(config?.base_budget) || 100000;
  return { id: null, name: "Base plan", season: defaultSeason(), budget: base, reserve_pct: 10, reserve_release: 0, reserve_release_note: "", group_targets: {}, group_exceptions: {}, allocations: {} };
}

export function seasonOptions() {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1, y + 2, y + 3].map((s) => `${s}-${String((s + 1) % 100).padStart(2, "0")}`);
}
export const defaultSeason = () => seasonOptions()[1];

const csvCell = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
export function downloadCsv(filename, header, rows) {
  const text = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
